import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import Portal from '@reach/portal'
import { useBodyLock } from '#src/common/hooks/useBodyLock'
import { useFocusTrap } from '#hooks'
import Translation from '#src/common/components/translation'
import {
  CloseButton,
  CrossIcon,
  FlyOutContainer,
  FlyOutHeader,
  FlyOutTitle,
  FlyOutTrigger,
  FlyOutTriggerButton
} from './styles'

const FlyOut = ({ children, titleText, trigger, additionalMenu }) => {
  const [open, setOpen] = useState(false)
  const [locked, setLocked] = useState(false)
  const sourceElement = useRef(null)
  const flyOutRef = useFocusTrap()
  const router = useRouter()

  useEffect(() => {
    if (open) {
      flyOutRef.current.focus()
    }
    // I would assume that flyOutRef.current needs to be in the dependencies... ?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (open) {
      handleClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  useBodyLock(locked)

  const handleTransitionEnd = () => {
    setLocked(open)
  }

  const handleClose = () => {
    setOpen(false)
    sourceElement.current.focus()
  }

  return (
    <>
      <FlyOutTrigger>
        <FlyOutTriggerButton
          type='button'
          ref={sourceElement}
          aria-expanded={open}
          aria-pressed={open}
          onClick={() => setOpen(true)}
        >
          {trigger || titleText}
        </FlyOutTriggerButton>
        {additionalMenu}
      </FlyOutTrigger>

      <Portal>
        <FlyOutContainer
          open={open}
          ref={flyOutRef}
          tabIndex={-1}
          data-testid='flyout-container'
          onTransitionEnd={handleTransitionEnd}
        >
          <FlyOutHeader>
            <FlyOutTitle level='h1' styleOverride='heading2Xs'>
              {titleText}
            </FlyOutTitle>
            <CloseButton type='button' open={open} onClick={handleClose}>
              <CrossIcon colour='grey-700' />
              <span className='sr-only'>
                <Translation id='web.common.close.button.accessibility' />
              </span>
            </CloseButton>
          </FlyOutHeader>
          {open ? children : null}
        </FlyOutContainer>
      </Portal>
    </>
  )
}

FlyOut.propTypes = {
  children: PropTypes.node.isRequired,
  titleText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  trigger: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
}

FlyOut.defaultProps = {
  trigger: null
}

export default FlyOut
